import { useMemo } from 'react'
import useTranslate from '@/hooks/use-translate'
import { FeatureNames } from '@/lib/featureFlags'
import { matchCommandBarQuery } from '../utils/matchCommandBarQuery'
import {
  COMMAND_BAR_ACTIONS,
  COMMAND_BAR_QUICK_ACTION_IDS,
  hasDeclarationHandler,
} from '../commandBarActions'
import { useCommandBarDeclaration } from '../context/CommandBarDeclarationContext'
import type {
  CommandBarItem,
  CommandBarRecentEntry,
  CommandBarSection,
} from '../types'
import { useFeatureFlags } from '@/hooks/use-feature-flags'

type EntityResults = {
  documents: CommandBarItem[]
  clients: CommandBarItem[]
  referentials: CommandBarItem[]
  flux: CommandBarItem[]
}

type UseCommandBarSectionsArgs = {
  query: string
  hasQuery: boolean
  entityResults: EntityResults
  recents: CommandBarRecentEntry[]
  showReferentials: boolean
}

function toActionItem(
  action: (typeof COMMAND_BAR_ACTIONS)[number],
  label: string,
  disabled = false
): CommandBarItem {
  return {
    id: `action-${action.id}`,
    type: 'action',
    label,
    actionId: action.id,
    shortcut: action.shortcut,
    disabled,
  }
}

export function useCommandBarSections({
  query,
  hasQuery,
  entityResults,
  recents,
  showReferentials,
}: UseCommandBarSectionsArgs) {
  const { t } = useTranslate('commandBar')
  const { isFeatureEnabled } = useFeatureFlags()
  const { handlers } = useCommandBarDeclaration()

  const showReferentialsFeature = isFeatureEnabled(FeatureNames.referentials)

  const filteredActions = useMemo(() => {
    return COMMAND_BAR_ACTIONS.filter((action) => {
      if (action.requiresReferentialsFeature && !showReferentialsFeature) {
        return false
      }
      if (
        action.isDeclarationContextual &&
        !hasDeclarationHandler(action, handlers)
      ) {
        return false
      }
      if (action.isQuickAction) {
        return false
      }
      return matchCommandBarQuery(query, t(action.labelKey))
    }).map((action) => {
      const disabled =
        action.requiresDeclaration === true &&
        !hasDeclarationHandler(action, handlers)
      return toActionItem(action, t(action.labelKey), disabled)
    })
  }, [handlers, query, showReferentialsFeature, t])

  const quickActions = useMemo(() => {
    if (hasQuery) return []

    return COMMAND_BAR_ACTIONS.filter(
      (action) =>
        action.isQuickAction &&
        COMMAND_BAR_QUICK_ACTION_IDS.includes(action.id) &&
        (!action.requiresReferentialsFeature || showReferentialsFeature)
    ).map((action) => toActionItem(action, t(action.labelKey)))
  }, [hasQuery, showReferentialsFeature, t])

  const declarationActions = useMemo(() => {
    if (hasQuery) return []

    return COMMAND_BAR_ACTIONS.filter(
      (action) =>
        action.isDeclarationContextual &&
        hasDeclarationHandler(action, handlers)
    ).map((action) => toActionItem(action, t(action.labelKey)))
  }, [handlers, hasQuery, t])

  const recentItems = useMemo<CommandBarItem[]>(() => {
    if (hasQuery) return []

    return recents.map((recent) => ({
      id: `recent-${recent.id}`,
      type: 'recent',
      label: recent.label,
      subtitle: recent.subtitle,
      actionId: recent.actionId,
      entityId: recent.id,
      path: recent.path,
      isRecent: true,
      // Lancé depuis un autre écran : grisé ici si l'écran n'en a pas le geste, comme dans les actions.
      disabled: !hasDeclarationHandler(
        COMMAND_BAR_ACTIONS.find(({ id }) => id === recent.actionId),
        handlers
      ),
    }))
  }, [handlers, hasQuery, recents])

  const sections = useMemo<CommandBarSection[]>(() => {
    const next: CommandBarSection[] = []

    if (hasQuery && filteredActions.length > 0) {
      next.push({
        id: 'actions',
        title: t('sectionActions'),
        items: filteredActions,
      })
    }

    if (!hasQuery) {
      if (declarationActions.length > 0) {
        next.push({
          id: 'on-declaration',
          title: t('sectionOnDeclaration'),
          items: declarationActions,
        })
      }

      if (quickActions.length > 0) {
        next.push({
          id: 'quick-actions',
          title: t('sectionQuickActions'),
          items: quickActions,
        })
      }

      if (recentItems.length > 0) {
        next.push({
          id: 'recents',
          title: t('sectionRecents'),
          items: recentItems,
        })
      }

      if (filteredActions.length > 0) {
        next.push({
          id: 'actions',
          title: t('sectionActions'),
          items: filteredActions,
        })
      }
    }

    if (hasQuery) {
      if (entityResults.documents.length > 0) {
        next.push({
          id: 'documents',
          title: t('sectionDocuments'),
          items: entityResults.documents,
        })
      }
      if (entityResults.clients.length > 0) {
        next.push({
          id: 'clients',
          title: t('sectionClients'),
          items: entityResults.clients,
        })
      }
      if (showReferentials && entityResults.referentials.length > 0) {
        next.push({
          id: 'referentials',
          title: t('sectionReferentials'),
          items: entityResults.referentials,
        })
      }
      if (entityResults.flux.length > 0) {
        next.push({
          id: 'flux',
          title: t('sectionFlux'),
          items: entityResults.flux,
        })
      }
    }

    return next
  }, [
    declarationActions,
    entityResults,
    filteredActions,
    hasQuery,
    quickActions,
    recentItems,
    showReferentials,
    t,
  ])

  const flatItems = useMemo(
    () => sections.flatMap((section) => section.items),
    [sections]
  )

  return { sections, flatItems }
}
